import React, { useEffect } from "react";
import {
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import { Button, Form, Input, Popconfirm } from "antd";
import TextArea from "antd/es/input/TextArea";

import ButtonWrapper from "../../../components/reuseComp/ButtonWrapper";

interface SectionItem {
  title: string;
  content: string;
  subtitle: string;
  points: string[];
}

interface WhatWeSolveProps {
  heading?: string;
  subtext?: string;
  sections?: SectionItem[];
}

interface Props {
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  onDelete: (index: number, field: "What We Solve") => void;
  form: any;
  whatWeSolveData?: WhatWeSolveProps;
  loading?: boolean;
  mode: "list" | "add" | "edit";
  setMode: (value: "list" | "add" | "edit") => void;
  editingIndex: number | null;
  setEditingIndex: (value: number | null) => void;
}

const WhatWeSolve: React.FC<Props> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  whatWeSolveData,
  onDelete,
  loading,
  mode,
  setMode,
  editingIndex,
  setEditingIndex,
}) => {
  const sections = whatWeSolveData?.sections || [];
  const disableSection = sections?.length >= 2;

  useEffect(() => {
    form.resetFields(["whatWeSolve"]);

    if (mode === "list") {
      form.setFieldsValue({
        whatWeSolve: {
          heading: whatWeSolveData?.heading || "",
          subtext: whatWeSolveData?.subtext || "",
          sections: sections.length
            ? sections
            : [{ title: "", content: "", subtitle: "", points: [""] }],
        },
      });
    }

    if (mode === "add") {
      form.setFieldsValue({
        whatWeSolve: {
          sections: [{ title: "", content: "", subtitle: "", points: [""] }],
        },
      });
    }

    if (mode === "edit" && editingIndex !== null) {
      const item = sections[editingIndex];
      if (item) {
        form.setFieldsValue({
          whatWeSolve: {
            sections: [
              {
                title: item.title,
                content: item.content,
                subtitle: item.subtitle,
                points: item.points.length ? item.points : [""],
              },
            ],
          },
        });
      }
    }
  }, [mode, editingIndex, whatWeSolveData]);

  return (
    <div>
      {isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl mb-4">
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Heading"
              name={["whatWeSolve", "heading"]}
              rules={[{ required: true, message: "Enter heading" }]}
            >
              <Input placeholder="enter heading..."/>
            </Form.Item>

            <Form.Item
              label="Subtext"
              name={["whatWeSolve", "subtext"]}
              rules={[{ required: true, message: "Enter subtext" }]}
            >
              <TextArea rows={3} placeholder="enter subtext..."/>
            </Form.Item>

            <div className="flex gap-3 mt-4">
              <ButtonWrapper
                type="primary"
                onClick={handleSave}
                loading={loading}
              >
                Save
              </ButtonWrapper>
              <ButtonWrapper
                onClick={() => {
                  setIsEditing(false);
                  handleCancel();
                }}
              >
                Cancel
              </ButtonWrapper>
            </div>
          </Form>
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="rounded-[20px] overflow-hidden">
          <div className="hidden sm:block">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Subtext</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>

              <tbody className="bg-[#F9F9F9]">
                {whatWeSolveData?.heading || whatWeSolveData?.subtext ? (
                  <tr className="border-b">
                    <td className="px-4 py-3">{whatWeSolveData?.heading}</td>
                    <td className="px-4 py-3">{whatWeSolveData?.subtext}</td>
                    <td className="px-4 py-3 text-right">
                      <EditOutlined
                        className="text-primary text-xl cursor-pointer"
                        onClick={() => setIsEditing(true)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center py-6">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}

                <tr>
                  <td colSpan={3} className="px-4 py-3 text-right">
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setMode("add");
                        setEditingIndex(null);
                      }}
                      className="text-primary font-semibold"
                      disabled={disableSection}
                    >
                      Add Sections
                    </Button>
                  </td>
                </tr>

                <tr>
                  <td colSpan={3} className="p-4">
                    <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-2 text-left">Title</th>
                          <th className="px-4 py-2 text-left">Description</th>
                          <th className="px-4 py-2 text-left">Subtitle</th>
                          <th className="px-4 py-2 text-left">Points</th>
                          <th className="px-4 py-2 text-right">Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {sections?.length ? (
                          sections.map((item, index) => (
                            <tr key={index} className="border-t">
                              <td className="px-4 py-2">{item.title}</td>
                              <td className="px-4 py-2">{item.content}</td>
                              <td className="px-4 py-2">{item.subtitle}</td>
                              <td className="px-4 py-2">
                                <p className="line-clamp-2">
                                  {item.points?.slice(0, 2).join(", ") || "-"}
                                </p>
                              </td>
                              <td className="px-4 py-2 text-right">
                                <div className="flex justify-end gap-4 text-lg">
                                  <EditOutlined
                                    className="cursor-pointer text-primary"
                                    onClick={() => {
                                      setMode("edit");
                                      setEditingIndex(index);
                                    }}
                                  />
                                  <Popconfirm
                                    title="Are you sure to delete this?"
                                    onConfirm={() =>
                                      onDelete(index, "What We Solve")
                                    }
                                  >
                                    <DeleteOutlined className="cursor-pointer text-red-500" />
                                  </Popconfirm>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={5}
                              className="px-4 py-6 text-center text-gray-500"
                            >
                              No sections added
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-4">
            <div className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm space-y-3">
              <div>
                <div>
                  <b className="text-primary">Heading:</b>
                </div>
                <p>{whatWeSolveData?.heading || "—"}</p>
              </div>
              <div>
                <div>
                  <b className="text-primary">Subtext:</b>
                </div>
                <p>{whatWeSolveData?.subtext || "—"}</p>
              </div>

              <div className="flex justify-end mt-2 text-primary">
                <EditOutlined
                  className="cursor-pointer text-[20px]"
                  onClick={() => setIsEditing(true)}
                />
              </div>
            </div>

            <div className="text-right">
              <Button
                type="dashed"
                className="text-primary font-semibold"
                icon={<PlusOutlined />}
                onClick={() => {
                  setMode("add");
                  setEditingIndex(null);
                }}
                disabled={disableSection}
              >
                Add Sections
              </Button>
            </div>

            {sections?.length ? (
              sections.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm space-y-3"
                >
                  <div>
                    <span className="font-semibold text-primary">Title:</span>
                    <p className="text-sm">{item.title}</p>
                  </div>
                  <div>
                    <span className="font-semibold text-primary">Subtitle:</span>
                    <p className="text-sm">{item.subtitle}</p>
                  </div>
                  <div>
                    <span className="font-semibold text-primary">Content:</span>
                    <p className="text-sm">{item.content}</p>
                  </div>
                  <div>
                    <span className="font-semibold text-primary">Points:</span>
                    <div className="px-5">
                      {item?.points?.map((point, index) => (
                        <li className="text-sm list-disc" key={index}>
                          {point || "-"}
                        </li>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end gap-4 mt-2 text-primary text-[20px]">
                    <EditOutlined
                      className="cursor-pointer"
                      onClick={() => {
                        setMode("edit");
                        setEditingIndex(index);
                      }}
                    />
                    <Popconfirm
                      title="Are you sure to delete this?"
                      onConfirm={() => onDelete(index, "What We Solve")}
                    >
                      <DeleteOutlined className="cursor-pointer text-red-500" />
                    </Popconfirm>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-6">
                No sections added
              </div>
            )}
          </div>
        </div>
      )}

      {mode !== "list" && !isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl">
          <Form layout="vertical" form={form}>
            <Form.List name={["whatWeSolve", "sections"]}>
              {(fields) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key} className="border p-4 rounded mb-4">
                      <Form.Item
                        label="Title"
                        name={[field.name, "title"]}
                        rules={[{ required: true, message: "Enter title" }]}
                      >
                        <Input placeholder="Enter title..." />
                      </Form.Item>

                      <Form.Item
                        label="Description"
                        name={[field.name, "content"]}
                        rules={[
                          { required: true, message: "Enter description" },
                        ]}
                      >
                        <TextArea rows={2} placeholder="enter description..."/>
                      </Form.Item>

                      <Form.Item
                        label="Subtitle"
                        name={[field.name, "subtitle"]}
                        rules={[{ required: true, message: "Enter subtitle" }]}
                      >
                        <Input placeholder="Enter subtitle..." />
                      </Form.Item>
                      <Form.Item label="Points" className="sm:col-span-2">
                        <Form.List name={[field.name, "points"]}>
                          {(pointFields, { add, remove }) => (
                            <>
                              {pointFields.map(({ key, name }) => (
                                <div
                                  key={key}
                                  className="flex gap-2 items-center mb-2"
                                >
                                  <Form.Item
                                    name={name}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Enter content",
                                      },
                                    ]}
                                    className="flex-1 mb-0"
                                  >
                                    <Input placeholder="Enter point..." />
                                  </Form.Item>
                                  <MinusCircleOutlined
                                    className="text-red-500 text-lg cursor-pointer"
                                    onClick={() => remove(name)}
                                  />
                                </div>
                              ))}
                              <Button
                                type="dashed"
                                onClick={() => add("")}
                                icon={<PlusOutlined />}
                                className="mt-2"
                              >
                                Add point
                              </Button>
                            </>
                          )}
                        </Form.List>
                      </Form.Item>
                    </div>
                  ))}
                </>
              )}
            </Form.List>

            <div className="flex gap-3 mt-4">
              <ButtonWrapper
                type="primary"
                onClick={handleSave}
                loading={loading}
              >
                Save
              </ButtonWrapper>
              <ButtonWrapper
                onClick={() => {
                  handleCancel();
                  setMode("list");
                  setEditingIndex(null);
                }}
              >
                Cancel
              </ButtonWrapper>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
};

export default WhatWeSolve;
